import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSearchContext } from "../../context/SearchContext";
import useLocationSearch, { useDebounce } from "../../hooks/useLocationSearch";
import { isKnownStateName, normalizeStateName } from "../../utils/locationState";
import { asArray, normalizePropertyCard } from "../mobileUtils";

const env = (typeof process !== "undefined" && process.env) || {};

const resolveApiBase = () => {
  const envBase = env.REACT_APP_API_BASE_URL || env.REACT_APP_API_URL || "";
  if (typeof window === "undefined") return envBase || "";
  if (!envBase) return "";
  if (env.NODE_ENV !== "production" && envBase.replace(/\/$/, "") === window.location.origin.replace(/\/$/, "")) {
    return "";
  }
  return envBase.replace(/\/$/, "");
};

const apiPath = (path) => `${resolveApiBase()}${path}`;

const futureDate = (offset) => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return date.toISOString().slice(0, 10);
};

const GUEST_OPTIONS = Array.from({ length: 50 }, (_, index) => index + 1);

export default function MobileSearchCard({ compact = false, onOpenFilters }) {
  const navigate = useNavigate();
  const { setSearch } = useSearchContext();
  const [params] = useSearchParams();
  const blurTimerRef = useRef(null);
  const [values, setValues] = useState({
    location: params.get("destination") || params.get("location") || params.get("search") || params.get("city") || "",
    checkIn: params.get("checkin") || params.get("checkIn") || futureDate(7),
    checkOut: params.get("checkout") || params.get("checkOut") || futureDate(9),
    guests: params.get("guests") || "2",
  });
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [propertyMatches, setPropertyMatches] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [activePanel, setActivePanel] = useState("");
  const debouncedLocation = useDebounce(values.location, 280);
  const {
    suggestions: locationMatches,
    loading: locationsLoading,
    error: locationsError,
    isPopular,
  } = useLocationSearch(values.location);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const query = String(debouncedLocation || "").trim();
    if (query.length < 2) {
      setPropertyMatches([]);
      setPropertiesLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const run = async () => {
      setPropertiesLoading(true);
      try {
        const search = new URLSearchParams({ destination: query, limit: "5" });
        const response = await fetch(apiPath(`/api/public/properties?${search.toString()}`), {
          signal: controller.signal,
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error("Unable to load matching stays.");
        const payload = await response.json();
        setPropertyMatches(asArray(payload).map(normalizePropertyCard).filter((item) => item.title));
      } catch (error) {
        if (error.name !== "AbortError") setPropertyMatches([]);
      } finally {
        if (!controller.signal.aborted) setPropertiesLoading(false);
      }
    };
    run();
    return () => controller.abort();
  }, [debouncedLocation]);

  const suggestions = useMemo(() => {
    const query = String(values.location || "").trim();
    const propertyItems =
      query.length >= 2
        ? propertyMatches.map((item) => ({
            id: `property:${item.id}`,
            type: "property",
            label: item.title,
            meta: item.location || "Homevilas stay",
            value: item.title,
            link: item.link,
          }))
        : [];
    const locationItems = locationMatches.map((item) => ({
      id: `location:${item.id || item.display_name}`,
      type: "location",
      label: item.display_name || item.city,
      meta: item.property_count ? `${item.property_count} stays` : item.state || item.country || "Destination",
      value: item.display_name || item.city,
      location: item,
    }));
    return [...locationItems, ...propertyItems].slice(0, query.length >= 2 ? 8 : 6);
  }, [locationMatches, propertyMatches, values.location]);

  const update = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (key === "location") {
      setSelectedSuggestion(null);
      setSuggestionsOpen(true);
    }
  };

  const selectSuggestion = (item) => {
    setSelectedSuggestion(item);
    setValues((current) => ({ ...current, location: item.value || item.label || "" }));
    setSuggestionsOpen(false);
  };

  const handleFocus = () => {
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
    setSuggestionsOpen(true);
  };

  const handleBlur = () => {
    blurTimerRef.current = window.setTimeout(() => setSuggestionsOpen(false), 140);
  };

  const submit = (event) => {
    event.preventDefault();
    if (values.checkIn && values.checkOut && values.checkOut <= values.checkIn) return;
    if (selectedSuggestion?.type === "property" && selectedSuggestion.link) {
      navigate(selectedSuggestion.link);
      return;
    }
    const next = new URLSearchParams();
    const selectedType = selectedSuggestion?.location?.type || "";
    const typedStateName = isKnownStateName(values.location) ? normalizeStateName(values.location) : "";
    const locationType = selectedType || (typedStateName ? "state" : "");
    const selectedCityName =
      selectedType && selectedType !== "state"
        ? String(selectedSuggestion?.location?.city || "").trim()
        : "";
    const locationValue = typedStateName || selectedCityName || values.location;
    if (values.location) {
      next.set("destination", locationValue);
      next.set("location", locationValue);
      next.set("search", locationValue);
      next.set("city", locationValue);
      if (locationType) next.set("location_type", locationType);
    }
    if (values.checkIn) {
      next.set("checkin", values.checkIn);
      next.set("checkIn", values.checkIn);
    }
    if (values.checkOut) {
      next.set("checkout", values.checkOut);
      next.set("checkOut", values.checkOut);
    }
    if (values.guests) {
      next.set("guests", values.guests);
      next.set("adults", values.guests);
    }
    setSearch((current) => ({
      ...current,
      type: "stays",
      locationLabel: locationValue,
      locationId: selectedSuggestion?.location?.id || "",
      locationSlug: selectedSuggestion?.location?.slug || "",
      locationType,
      checkin: values.checkIn,
      checkIn: values.checkIn,
      checkout: values.checkOut,
      checkOut: values.checkOut,
      guests: Number(values.guests),
      adults: Number(values.guests),
      children: 0,
    }));
    navigate(`/stays${next.toString() ? `?${next.toString()}` : ""}`);
  };

  const openFilters = () => {
    if (typeof onOpenFilters === "function") {
      onOpenFilters();
      return;
    }
    navigate("/stays");
  };

  const togglePanel = (panel) => {
    setActivePanel((current) => (current === panel ? "" : panel));
  };

  const suggestionsMarkup = suggestionsOpen ? (
    <div className="hv-mobile-suggestions">
      {locationsLoading || propertiesLoading ? <div className="hv-mobile-suggestion-empty">Loading...</div> : null}
      {!locationsLoading && !propertiesLoading && suggestions.length ? (
        <>
          <div className="hv-mobile-suggestion-head">
            {isPopular && String(values.location || "").trim().length < 2 ? "Popular destinations" : "Search results"}
          </div>
          {suggestions.map((item) => (
            <button
              type="button"
              key={item.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectSuggestion(item)}
            >
              <i className={`bi ${item.type === "property" ? "bi-house-door" : "bi-geo-alt"}`} aria-hidden="true" />
              <span>
                <strong>{item.label}</strong>
                <small>{item.meta}</small>
              </span>
            </button>
          ))}
        </>
      ) : null}
      {!locationsLoading && !propertiesLoading && !suggestions.length ? (
        <div className="hv-mobile-suggestion-empty">No matching location or stay found.</div>
      ) : null}
      {locationsError ? <div className="hv-mobile-suggestion-error">{locationsError}</div> : null}
    </div>
  ) : null;

  if (compact) {
    return (
      <div className="hv-mobile-search">
        <form className="hv-mobile-search-card" onSubmit={submit}>
          <div className="hv-mobile-search-top">
            <div className="hv-mobile-destination">
              <label htmlFor="mobile-location-compact">Location</label>
              <i className="bi bi-geo-alt-fill hv-mobile-location-icon" aria-hidden="true" />
              <span className="hv-mobile-destination-text">
                <input
                  id="mobile-location-compact"
                  className="hv-mobile-destination-input"
                  value={values.location}
                  onChange={(event) => update("location", event.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="Goa, Kerala, Udaipur"
                  autoComplete="off"
                  aria-label="Select destination"
                />
                <span className="hv-mobile-destination-subtitle">Where are you going?</span>
              </span>
              {suggestionsMarkup}
            </div>

            <button className="hv-mobile-search-button" type="submit" aria-label="Search">
              <i className="bi bi-search" aria-hidden="true" />
            </button>
          </div>

          <div className="hv-mobile-search-chips">
            <button type="button" className="hv-mobile-search-chip" data-search-action="filters" onClick={openFilters}>
              <i className="bi bi-sliders hv-chip-icon" aria-hidden="true" />
              <span className="hv-chip-label">Filters</span>
            </button>
            <button type="button" className={`hv-mobile-search-chip ${activePanel === "dates" ? "is-active active" : ""}`} data-search-action="dates" onClick={() => togglePanel("dates")}>
              <i className="bi bi-calendar3 hv-chip-icon" aria-hidden="true" />
              <span className="hv-chip-label">Dates</span>
            </button>
            <button type="button" className={`hv-mobile-search-chip ${activePanel === "guests" ? "is-active active" : ""}`} data-search-action="guests" onClick={() => togglePanel("guests")}>
              <i className="bi bi-people hv-chip-icon" aria-hidden="true" />
              <span className="hv-chip-label">{values.guests} Guests</span>
              <i className="bi bi-chevron-down hv-chip-caret" aria-hidden="true" />
            </button>
            <button type="button" className="hv-mobile-search-chip" data-search-action="price" onClick={openFilters}>
              <i className="bi bi-tag hv-chip-icon" aria-hidden="true" />
              <span className="hv-chip-label">Price</span>
            </button>
            <button type="button" className="hv-mobile-search-chip" data-search-action="more" onClick={openFilters}>
              <i className="bi bi-three-dots hv-chip-icon" aria-hidden="true" />
              <span className="hv-chip-label">More</span>
            </button>
          </div>

          {activePanel === "dates" ? (
            <div className="hv-mobile-search-panel hv-mobile-date-panel">
              <label>Check-in<input type="date" value={values.checkIn} onChange={(event) => update("checkIn", event.target.value)} /></label>
              <label>Check-out<input type="date" value={values.checkOut} onChange={(event) => update("checkOut", event.target.value)} /></label>
            </div>
          ) : null}
          {activePanel === "guests" ? (
            <div className="hv-mobile-search-panel">
              <label>Guests<select value={values.guests} onChange={(event) => update("guests", event.target.value)}>
                {GUEST_OPTIONS.map((count) => (
                  <option key={count} value={count}>{count} guests</option>
                ))}
              </select></label>
            </div>
          ) : null}
        </form>
      </div>
    );
  };

  return (
    <form className={`hv-search-card ${compact ? "hv-search-card-compact" : ""}`} onSubmit={submit}>
      <div className="hv-field hv-search-location hv-field-icon">
        <label htmlFor={compact ? "mobile-location-compact" : "mobile-location"}>Location</label>
        <i className="bi bi-geo-alt" aria-hidden="true" /><input
          id={compact ? "mobile-location-compact" : "mobile-location"}
          value={values.location}
          onChange={(event) => update("location", event.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Goa, Kerala, Udaipur"
          autoComplete="off"
        />
        {compact ? <span className="hv-search-field-hint">Where are you going?</span> : null}
        {suggestionsMarkup}
      </div>
      {!compact ? <div className="hv-mini-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        <div className="hv-field hv-field-icon">
          <label htmlFor="mobile-checkin">Check-in</label>
          <i className="bi bi-calendar3" aria-hidden="true" /><input id="mobile-checkin" type="date" value={values.checkIn} onChange={(event) => update("checkIn", event.target.value)} />
        </div>
        <div className="hv-field hv-field-icon">
          <label htmlFor="mobile-checkout">Check-out</label>
          <i className="bi bi-calendar3" aria-hidden="true" /><input id="mobile-checkout" type="date" value={values.checkOut} onChange={(event) => update("checkOut", event.target.value)} />
        </div>
      </div> : null}
      {!compact ? <div className="hv-field hv-search-guests hv-field-icon">
        <label htmlFor="mobile-guests">Guests</label>
        <i className="bi bi-people" aria-hidden="true" /><select id="mobile-guests" value={values.guests} onChange={(event) => update("guests", event.target.value)}>
          {GUEST_OPTIONS.map((count) => (
            <option key={count} value={count}>{count} guests</option>
          ))}
        </select>
      </div> : null}
      <button className="hv-primary-btn" type="submit">
        <i className="bi bi-search me-2" aria-hidden="true" />
        Search Stays
      </button>
      {compact ? (
        <>
          <div className="hv-filter-chips">
            <button type="button" onClick={openFilters}><i className="bi bi-sliders" /> <span>Filters</span></button>
            <button type="button" onClick={() => togglePanel("dates")} className={activePanel === "dates" ? "active" : ""}><i className="bi bi-calendar3" /> <span>Dates</span></button>
            <button type="button" onClick={() => togglePanel("guests")} className={activePanel === "guests" ? "active" : ""}><i className="bi bi-people" /> <span>{values.guests} Guests</span><i className="bi bi-chevron-down hv-chip-caret" /></button>
            <button type="button" onClick={openFilters}><i className="bi bi-tag" /> <span>Price</span></button>
            <button type="button" onClick={openFilters}><i className="bi bi-three-dots" /> <span>More</span></button>
          </div>
          {activePanel === "dates" ? (
            <div className="hv-compact-panel hv-compact-date-panel">
              <label>Check-in<input type="date" value={values.checkIn} onChange={(event) => update("checkIn", event.target.value)} /></label>
              <label>Check-out<input type="date" value={values.checkOut} onChange={(event) => update("checkOut", event.target.value)} /></label>
            </div>
          ) : null}
          {activePanel === "guests" ? (
            <div className="hv-compact-panel">
              <label>Guests<select value={values.guests} onChange={(event) => update("guests", event.target.value)}>
                {GUEST_OPTIONS.map((count) => (
                  <option key={count} value={count}>{count} guests</option>
                ))}
              </select></label>
            </div>
          ) : null}
        </>
      ) : null}
    </form>
  );
}
